import React, { useEffect, useState } from 'react';
import { Facebook, Instagram, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { metaSocialAPI } from '../lib/api';

const FORMATOS = [['feed', 'Post (imagen)'], ['carrusel', 'Carrusel'], ['reel', 'Reel (vídeo)'], ['story', 'Story (a mano)']];
const defaultWhen = (fecha) => `${fecha || new Date(Date.now() + 86400000).toISOString().slice(0, 10)}T10:00`;

export const MetaScheduleDialog = ({ open, post, files, onClose, onDone }) => {
  const [canales, setCanales] = useState(['instagram']);
  const [formato, setFormato] = useState('feed');
  const [when, setWhen] = useState('');
  const [sel, setSel] = useState([]);
  const [cover, setCover] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const vids = files.filter((f) => f.kind === 'video');
    setFormato(vids.length ? 'reel' : files.length > 1 ? 'carrusel' : 'feed');
    setSel(vids.length ? [vids[0].id] : files.filter((f) => f.kind === 'image').map((f) => f.id));
    setWhen(defaultWhen(post.fecha)); setCaption(post.copy || ''); setCover(''); setError('');
  }, [open, post, files]);

  const toggleFile = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleCanal = (c) => setCanales((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const pub = await metaSocialAPI.schedule(post.id, { canales: formato === 'story' ? ['instagram'] : canales, formato, publish_at: when, media_file_ids: sel, cover_file_id: formato === 'reel' && cover ? cover : null, caption });
      toast.success(formato === 'story' ? 'Story marcada para publicar a mano: Ana recibirá el aviso ese día' : 'Programada en Meta');
      onDone(new Date(pub.publish_at).toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' }));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="meta-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Programar en Instagram / Facebook</DialogTitle>
          <DialogDescription>Instagram se publica a la hora indicada con hasta 15 min de margen; Facebook a la hora exacta.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FORMATOS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFormato(v)} data-testid={`meta-format-${v}`}
                className={`px-3 h-9 rounded-full border text-sm transition-colors ${formato === v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{l}</button>
            ))}
          </div>
          {formato === 'story' ? (
            <p className="text-sm rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900" data-testid="meta-story-note">Las stories no se publican desde el portal: quedará en «Programado» y Ana recibirá un aviso el día indicado para publicarla a mano.</p>
          ) : (
            <div className="flex gap-4">
              {[['instagram', 'Instagram', Instagram], ['facebook', 'Facebook', Facebook]].map(([v, l, Icon]) => (
                <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={canales.includes(v)} onCheckedChange={() => toggleCanal(v)} data-testid={`meta-channel-${v}`} /><Icon className="w-4 h-4" />{l}
                </label>
              ))}
            </div>
          )}
          <div className="space-y-1.5"><Label>Fecha y hora (hora de España)</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-[240px]" data-testid="meta-when-input" /></div>
          <div className="space-y-1.5"><Label>Archivos {formato === 'carrusel' ? '(en el orden en que los marques)' : ''}</Label>
            {!files.length && <p className="text-sm text-red-600">La publicación no tiene archivos subidos.</p>}
            {files.map((f) => (
              <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`meta-file-${f.id}`}>
                <Checkbox checked={sel.includes(f.id)} onCheckedChange={() => toggleFile(f.id)} />
                {sel.includes(f.id) && formato === 'carrusel' && <span className="text-xs font-semibold text-primary">{sel.indexOf(f.id) + 1}</span>}
                <span className="truncate">{f.original_filename}</span><span className="text-xs text-muted-foreground">{f.kind === 'video' ? 'vídeo' : f.kind === 'image' ? 'imagen' : f.kind}</span>
              </label>
            ))}
          </div>
          {formato === 'reel' && (
            <div className="space-y-1.5"><Label>Portada del reel (opcional)</Label>
              <select value={cover} onChange={(e) => setCover(e.target.value)} className="h-9 rounded-md border px-2 text-sm bg-background" data-testid="meta-cover-select">
                <option value="">Fotograma por defecto</option>
                {files.filter((f) => f.kind === 'image').map((f) => <option key={f.id} value={f.id}>{f.original_filename}</option>)}
              </select></div>
          )}
          <div className="space-y-1.5"><Label>Copy con hashtags</Label>
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5} maxLength={2200} data-testid="meta-caption-input" />
            <p className="text-xs text-muted-foreground text-right">{caption.length}/2200</p></div>
          {error && <p className="text-sm rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 whitespace-pre-wrap" data-testid="meta-schedule-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || !sel.length || (formato !== 'story' && !canales.length) || !when} data-testid="meta-schedule-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{formato === 'story' ? 'Marcar para publicar a mano' : 'Programar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
